/**
 * HTML Sanitization utilities to prevent XSS attacks
 */

/**
 * Escapes HTML special characters to prevent XSS
 * Use this for plain text content that should not contain any HTML
 */
export function escapeHTML(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Sanitizes HTML content by allowing only safe tags and attributes
 * Use this for content that needs basic HTML formatting (bold, links, lists)
 */
export function sanitizeHTML(dirty: string): string {
  const allowedTags = ['p', 'br', 'strong', 'a', 'ul', 'ol', 'li', 'em', 'b', 'i', 'div'];
  const allowedAttributes: { [key: string]: string[] } = {
    a: ['href', 'target', 'rel'],
    div: ['class'],
  };

  // Create a temporary div to parse the HTML
  const temp = document.createElement('div');
  temp.innerHTML = dirty;

  // Recursively clean the DOM tree
  function cleanNode(node: Node): Node | null {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.cloneNode(true);
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Check if tag is allowed
      if (!allowedTags.includes(tagName)) {
        // For disallowed tags, return their text content
        return document.createTextNode(element.textContent || '');
      }

      // Create new element with only allowed attributes
      const newElement = document.createElement(tagName);

      if (allowedAttributes[tagName]) {
        for (const attr of allowedAttributes[tagName]) {
          const value = element.getAttribute(attr);
          if (value) {
            // Special handling for href to prevent javascript: URLs
            if (attr === 'href') {
              const sanitizedUrl = sanitizeURL(value);
              if (sanitizedUrl !== '#') {
                newElement.setAttribute(attr, sanitizedUrl);
              }
            } else {
              newElement.setAttribute(attr, value);
            }
          }
        }
      }

      // Process child nodes
      for (let i = 0; i < element.childNodes.length; i++) {
        const cleanedChild = cleanNode(element.childNodes[i]);
        if (cleanedChild) {
          newElement.appendChild(cleanedChild);
        }
      }

      return newElement;
    }

    return null;
  }

  const cleaned = document.createElement('div');
  for (let i = 0; i < temp.childNodes.length; i++) {
    const cleanedNode = cleanNode(temp.childNodes[i]);
    if (cleanedNode) {
      cleaned.appendChild(cleanedNode);
    }
  }

  return cleaned.innerHTML;
}

/**
 * Validates and sanitizes URLs to prevent javascript: and other dangerous protocols
 */
export function sanitizeURL(url: string): string {
  const trimmed = url.trim();

  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];

  const lowerUrl = trimmed.toLowerCase();
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return '#';
    }
  }

  // Try to parse as URL
  try {
    const parsed = new URL(trimmed, window.location.href);

    // Only allow http and https
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return '#';
    }

    return parsed.href;
  } catch {
    // If it's a relative URL, that's okay
    if (trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../')) {
      return trimmed;
    }

    // Invalid URL
    return '#';
  }
}
